import React, { useEffect, useMemo, useRef, useState } from "react";

const BASE_CANVAS = { width: 960, height: 320 };
const EXPANDED_CANVAS = { width: 1200, height: 560 };
const NODE_RADIUS = 14;
const MIN_SCALE = 0.6;
const MAX_SCALE = 2.6;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const truncateLabel = (label, max = 22) => {
  if (!label) return "-";
  if (label.length <= max) return label;
  return `${label.slice(0, max - 3)}...`;
};

const buildPositions = (nodes, columns, width, height) => {
  const columnNodes = columns.map((column) =>
    nodes.filter((node) => node.group === column)
  );
  const positions = {};
  const xStep = width / (columns.length + 1);

  columnNodes.forEach((groupNodes, columnIndex) => {
    const x = (columnIndex + 1) * xStep;
    const yStep = height / Math.max(groupNodes.length + 1, 2);
    groupNodes.forEach((node, index) => {
      positions[node.id] = { x, y: (index + 1) * yStep };
    });
  });

  return positions;
};

export default function GraphPanel({
  title,
  description,
  columns,
  nodes,
  links,
  legend
}) {
  const [expanded, setExpanded] = useState(false);
  const [view, setView] = useState({ scale: 1, offsetX: 0, offsetY: 0 });
  const dragRef = useRef(null);
  const svgRef = useRef(null);

  const canvas = expanded ? EXPANDED_CANVAS : BASE_CANVAS;

  const basePositions = useMemo(
    () => buildPositions(nodes, columns, canvas.width, canvas.height),
    [nodes, columns, canvas.width, canvas.height]
  );

  const [positions, setPositions] = useState(basePositions);

  useEffect(() => {
    setPositions(basePositions);
  }, [basePositions]);

  useEffect(() => {
    setView({ scale: 1, offsetX: 0, offsetY: 0 });
  }, [expanded, nodes.length, columns.length]);

  const renderedLinks = links
    .map((link) => {
      const source = positions[link.from];
      const target = positions[link.to];
      if (!source || !target) return null;
      return {
        ...link,
        source,
        target
      };
    })
    .filter(Boolean);

  const handleWheel = (event) => {
    event.preventDefault();
    const delta = event.deltaY < 0 ? 0.12 : -0.12;
    const rect = event.currentTarget.getBoundingClientRect();
    const pointerX = event.clientX - rect.left;
    const pointerY = event.clientY - rect.top;

    setView((prev) => {
      const nextScale = clamp(prev.scale + delta, MIN_SCALE, MAX_SCALE);
      const scaleFactor = nextScale / prev.scale;
      const offsetX = pointerX - scaleFactor * (pointerX - prev.offsetX);
      const offsetY = pointerY - scaleFactor * (pointerY - prev.offsetY);
      return { scale: nextScale, offsetX, offsetY };
    });
  };

  const handlePointerDown = (event) => {
    if (event.button !== 0) return;
    dragRef.current = {
      type: "pan",
      startX: event.clientX,
      startY: event.clientY,
      originX: view.offsetX,
      originY: view.offsetY
    };
    svgRef.current?.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag) return;

    if (drag.type === "pan") {
      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      setView((prev) => ({
        ...prev,
        offsetX: drag.originX + dx,
        offsetY: drag.originY + dy
      }));
      return;
    }

    if (drag.type === "node") {
      const dx = (event.clientX - drag.startX) / view.scale;
      const dy = (event.clientY - drag.startY) / view.scale;
      setPositions((prev) => ({
        ...prev,
        [drag.nodeId]: { x: drag.originX + dx, y: drag.originY + dy }
      }));
    }
  };

  const handlePointerUp = (event) => {
    if (dragRef.current) {
      dragRef.current = null;
      svgRef.current?.releasePointerCapture(event.pointerId);
    }
  };

  const handleNodePointerDown = (event, nodeId) => {
    event.stopPropagation();
    const position = positions[nodeId];
    if (!position) return;
    dragRef.current = {
      type: "node",
      nodeId,
      startX: event.clientX,
      startY: event.clientY,
      originX: position.x,
      originY: position.y
    };
    svgRef.current?.setPointerCapture(event.pointerId);
  };

  const handleReset = () => {
    setPositions(basePositions);
    setView({ scale: 1, offsetX: 0, offsetY: 0 });
  };

  const handleZoom = (direction) => {
    setView((prev) => ({
      ...prev,
      scale: clamp(prev.scale + direction * 0.2, MIN_SCALE, MAX_SCALE)
    }));
  };

  const card = (
    <div
      className={`graph-card${expanded ? " expanded" : ""}`}
      onClick={expanded ? (event) => event.stopPropagation() : undefined}
    >
      <div className="graph-header">
        <div>
          <div className="graph-title">{title}</div>
          <div className="graph-subtitle">{description}</div>
        </div>
        <div className="graph-controls">
          <button className="graph-button" type="button" onClick={handleReset}>
            Reset View
          </button>
          <button className="graph-button" type="button" onClick={() => handleZoom(1)}>
            Zoom +
          </button>
          <button className="graph-button" type="button" onClick={() => handleZoom(-1)}>
            Zoom -
          </button>
          <button
            className="graph-button"
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
          >
            {expanded ? "Close" : "Expand"}
          </button>
        </div>
      </div>

      {legend && (
        <div className="graph-legend">
          {legend.map((item) => (
            <div key={item.label} className={`graph-legend-item ${item.className}`}>
              <span className="graph-dot" />
              {item.label}
            </div>
          ))}
        </div>
      )}

      <div className="graph-body">
        {nodes.length === 0 ? (
          <div className="graph-empty">No graph data available yet.</div>
        ) : (
          <svg
            ref={svgRef}
            className="graph-canvas"
            viewBox={`0 0 ${canvas.width} ${canvas.height}`}
            role="img"
            aria-label={title}
            onWheel={handleWheel}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerUp}
          >
            <g transform={`translate(${view.offsetX} ${view.offsetY}) scale(${view.scale})`}>
              {renderedLinks.map((link, index) => (
                <line
                  key={`${link.from}-${link.to}-${index}`}
                  x1={link.source.x}
                  y1={link.source.y}
                  x2={link.target.x}
                  y2={link.target.y}
                  className="graph-link"
                />
              ))}
              {nodes.map((node) => {
                const position = positions[node.id];
                if (!position) return null;
                return (
                  <g
                    key={node.id}
                    className={`graph-node ${node.group}`}
                    onPointerDown={(event) => handleNodePointerDown(event, node.id)}
                  >
                    <circle cx={position.x} cy={position.y} r={NODE_RADIUS} />
                    <text x={position.x + NODE_RADIUS + 6} y={position.y + 4}>
                      {truncateLabel(node.label)}
                    </text>
                  </g>
                );
              })}
            </g>
          </svg>
        )}
        <div className="graph-hint">Drag to pan. Scroll to zoom. Drag nodes to reposition.</div>
      </div>

    </div>
  );

  return (
    <>
      {expanded && <div className="graph-overlay" onClick={() => setExpanded(false)} />}
      {card}
    </>
  );
}
